import { useRef, useMemo, useState, useEffect } from 'react'
import { StoreList } from '@/components/StoreList'
import { FocusTrap } from '@/components/ui/FocusTrap'
import { NoSearchResults } from '@/components/EmptyStates'
import { 
  MapIcon, 
  MagnifyingGlassIcon, 
  FilterIcon,
  SortIcon,
  XIcon,
} from '@/components/ui/icons'
import type { Store } from '@/types'
import { useStoreDistances } from '@/hooks/useStoreDistances'
import { getStorePickupStatus, storeOffersDeliveryToday } from '@/utils/storePickupStatus'

type SortOption = 'distance' | 'fastest' | 'name'

export interface PickupContentProps {
  filteredStores: Store[]
  onSelect?: (store: Store) => void
  onViewMap: () => void
  loading: boolean
}

export function PickupContent({ 
  filteredStores,
  onSelect, 
  onViewMap, 
  loading,
}: PickupContentProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const { stores: storesWithDistances } = useStoreDistances(filteredStores)

  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<SortOption>('distance')
  const [filters, setFilters] = useState({ 
    openNow: false, 
    hasDelivery: false, 
    hasFreeDelivery: false 
  })
  const [showFilters, setShowFilters] = useState(false)

  const onSearchChange = (query: string) => {
    setSearchQuery(query)
  }

  const onFilterChange = (filter: Partial<typeof filters>) => {
    setFilters(prev => ({ ...prev, ...filter }))
  }

  const onClearFilters = () => {
    setFilters({ openNow: false, hasDelivery: false, hasFreeDelivery: false })
  }

  const activeFiltersCount = [
    filters.openNow,
    filters.hasDelivery,
    filters.hasFreeDelivery,
  ].filter(Boolean).length

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showFilters) {
        setShowFilters(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [showFilters])

  const displayedStores = useMemo(() => {
    if (!storesWithDistances) return []

    let stores = [...storesWithDistances]

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim()
      stores = stores.filter((store: Store) => {
        const name = (store.name ?? '').toLowerCase()
        const area = (store.area ?? '').toLowerCase()
        const street = (store.street ?? '').toLowerCase()
        return name.includes(query) || area.includes(query) || street.includes(query)
      })
    }

    // Filter: Open Now — exclude closed stores
    if (filters.openNow) {
      stores = stores.filter((store: Store) => {
        const { status } = getStorePickupStatus(store)
        return status === 'open' || status === 'closing_soon'
      })
    }

    // Filter: Offers Delivery — exclude stores with noDelivery flag
    if (filters.hasDelivery) {
      stores = stores.filter((store: Store) => storeOffersDeliveryToday(store))
    }

    // Sorting
    switch (sortBy) {
      case 'distance':
        stores.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity))
        break
      case 'fastest':
        stores.sort((a, b) => (a.travelTime ?? Infinity) - (b.travelTime ?? Infinity))
        break
      // case 'rating':
      //   stores.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0))
      //   break
      case 'name':
        stores.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
        break
    }

    return stores
  }, [storesWithDistances, searchQuery, sortBy, filters])

  return (
    <>
      {/* Search Bar */}
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-text-tertiary" />
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search stores..."
            className="
              w-full py-3 pl-10 pr-4
              text-sm/5 text-text-primary
              bg-surface-elevated
              border border-border-light
              rounded-xl
              placeholder:text-text-tertiary
              focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent
              transition-all duration-200
            "
            aria-label="Search stores by name or location"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-surface-tertiary"
              aria-label="Clear search"
            >
              <XIcon className="size-4 text-text-tertiary" />
            </button>
          )}
        </div>
        
        {/* Filter Toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          aria-expanded={showFilters}
          aria-label={`Filters${activeFiltersCount > 0 ? `, ${activeFiltersCount} active` : ''}`}
          className={`
            relative p-3 rounded-xl border transition-all duration-200
            ${showFilters 
              ? 'bg-brand-primary text-white border-brand-primary' 
              : 'bg-surface-elevated text-text-secondary border-border-light hover:border-border-default'
            }
          `}
        >
          <FilterIcon className="size-5" />
          {activeFiltersCount > 0 && !showFilters && (
            <span className="absolute -top-1 -right-1 size-5 flex items-center justify-center text-xs font-bold bg-brand-primary text-white rounded-full">
              {activeFiltersCount}
            </span>
          )}
        </button>
      </div>

      {/* Filter Panel with Focus Trap */}
      {showFilters && (
        <FocusTrap active={showFilters}>
          <div 
            className="mb-4 p-4 bg-surface-elevated rounded-xl border border-border-light shadow-card animate-fade-in-up"
            role="dialog"
            aria-label="Filter options"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-text-primary">Filters</h3>
              <div className="flex items-center gap-2">
                {activeFiltersCount > 0 && (
                  <button
                    onClick={onClearFilters}
                    className="text-sm/5 text-brand-primary hover:underline"
                  >
                    Clear all
                  </button>
                )}
                <button
                  onClick={() => setShowFilters(false)}
                  aria-label="Close filters"
                  className="p-1 rounded-lg hover:bg-surface-tertiary transition-colors"
                >
                  <XIcon className="size-5 text-text-tertiary" />
                </button>
              </div>
            </div>
            
            {/* Filter Chips */}
            <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label="Filter options">
              <FilterChip
                label="Open now"
                active={filters.openNow}
                onClick={() => onFilterChange({ openNow: !filters.openNow })}
              />
              <FilterChip
                label="Offers delivery"
                active={filters.hasDelivery}
                onClick={() => onFilterChange({ hasDelivery: !filters.hasDelivery })}
              />
              <FilterChip
                label="Free delivery"
                active={filters.hasFreeDelivery}
                onClick={() => onFilterChange({ hasFreeDelivery: !filters.hasFreeDelivery })}
              />
            </div>

            {/* Sort Options */}
            <div className="border-t border-border-light pt-3">
              <div className="flex items-center gap-2 mb-2">
                <SortIcon className="size-4 text-text-tertiary" />
                <span className="text-sm/5 font-medium text-text-secondary">Sort by</span>
              </div>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Sort order">
                {[
                  { value: 'distance', label: 'Nearest' },
                  { value: 'fastest', label: 'Fastest' },
                  { value: 'name', label: 'A-Z' },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSortBy(option.value as SortOption)}
                    role="radio"
                    aria-checked={sortBy === option.value}
                    className={`
                      px-3 py-1.5 text-sm/5 rounded-lg transition-all duration-200
                      ${sortBy === option.value
                        ? 'bg-brand-primary text-white'
                        : 'bg-surface-tertiary text-text-secondary hover:bg-surface-secondary'
                      }
                    `}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </FocusTrap>
      )}

      {/* Results Count */}
      <p className="text-sm/5 text-text-secondary mb-4">
        {searchQuery || activeFiltersCount > 0 
          ? `${displayedStores.length} stores found`
          : 'Select a location to pick up your order'
        }
      </p>

      <StoreList
        stores={displayedStores}
        variant="pickup"
        onSelect={onSelect}
        loading={loading}
        showDetails={true}
      />

      {/* No Results - Use EmptyState with illustration */}
      {displayedStores.length === 0 && !loading && (
        <NoSearchResults 
          query={searchQuery || 'your filters'} 
          onClearFilters={onClearFilters} 
        />
      )}

      {/* View on Map Button */}
      {displayedStores.length > 0 && (
        <button
          onClick={onViewMap}
          className="
            w-full mt-4 py-3 px-4
            flex items-center justify-center gap-2
            text-brand-primary font-medium
            bg-surface-elevated
            border border-border-light
            rounded-xl shadow-card
            hover:shadow-card-hover hover:border-border-default
            transition-all duration-200
            active:scale-[0.99]
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary
          "
        >
          <MapIcon className="size-5" />
          View on map
        </button>
      )}
    </>
  )
}

interface FilterChipProps {
  label: string
  active: boolean
  onClick: () => void
}

function FilterChip({ label, active, onClick }: FilterChipProps) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={`
        px-3 py-1.5 text-sm/5 rounded-full border transition-all duration-200
        ${active
          ? 'bg-brand-primary/10 text-brand-primary border-brand-primary'
          : 'bg-transparent text-text-secondary border-border-default hover:border-border-strong'
        }
      `}
    >
      {label}
    </button>
  )
}
